import React from 'react';
import {
  Copy,
  ThumbsUp,
  ThumbsDown,
  Sparkle,
  FileText,
  Image as ImageIcon,
  File,
  FileCode,
} from 'lucide-react';
import { Turn } from '../../types/conversation';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { tomorrow } from 'react-syntax-highlighter/dist/cjs/styles/prism';
import SuggestionSkeleton from './SuggestionSkeleton';

interface FileMetadata {
  file_id: string;
  file_name: string;
  file_type: string;
}

interface TurnViewProps {
  turn: Turn;
  streaming?: boolean;
  onEdit?: (content: string) => void;
  onCopy?: (content: string) => void;
  onLike?: (type: 'like' | 'dislike') => void;
  likeStatus?: 'like' | 'dislike' | null;
  showSuggestions?: boolean;
  suggestedActions?: string[];
  suggestedActionsLoading?: boolean;
  onSuggestedAction?: (action: string) => void;
  attachments?: FileMetadata[]; // File metadata for attachments
}

const TurnView: React.FC<TurnViewProps> = ({
  turn,
  streaming = false,
  onEdit: _onEdit,
  onCopy: _onCopy,
  onLike,
  likeStatus = null,
  showSuggestions = false,
  suggestedActions = [],
  suggestedActionsLoading = false,
  onSuggestedAction,
  attachments = [],
}) => {
  const { t } = useTranslation();

  // Debug: Log attachments prop
  console.log(`TurnView ${turn.id}: attachments =`, attachments);

  const formatTime = (timestamp: number) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Get file icon based on file name extension
  const getFileIcon = (fileName: string) => {
    const ext = fileName.split('.').pop()?.toLowerCase() || '';

    if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'].includes(ext)) {
      return <ImageIcon size={20} className='text-blue-600' />;
    } else if (ext === 'pdf') {
      return <FileText size={20} className='text-red-600' />;
    } else if (['doc', 'docx'].includes(ext)) {
      return <FileText size={20} className='text-blue-700' />;
    } else if (['xls', 'xlsx'].includes(ext)) {
      return <FileText size={20} className='text-green-600' />;
    } else if (['ppt', 'pptx'].includes(ext)) {
      return <FileText size={20} className='text-orange-600' />;
    } else if (
      [
        'txt',
        'md',
        'json',
        'xml',
        'js',
        'ts',
        'jsx',
        'tsx',
        'py',
        'java',
        'c',
        'cpp',
        'go',
        'rs',
      ].includes(ext)
    ) {
      return <FileCode size={20} className='text-gray-600' />;
    } else {
      return <File size={20} className='text-gray-600' />;
    }
  };

  const isStreaming = streaming || turn.status === 'streaming';
  const hasAnswer = turn.answerText.trim().length > 0 || isStreaming;
  const hasError = turn.status === 'failed' && turn.error;

  // Determine if we should show streaming animation
  // Show in two cases:
  // 1. Waiting for response to start (no content, no thinking yet)
  // 2. Streaming content has arrived (has answer text), but NOT during pure thinking phase
  const hasThinking = turn.thinking && turn.thinking.trim().length > 0;
  const hasContentArrived = turn.answerText.trim().length > 0;
  const showStreamingAnimation = isStreaming && !hasThinking && !hasContentArrived; // Show when waiting (no thinking, no content)

  // Clean thinking content by removing markers
  const cleanThinkingContent = (content: string): string => {
    return content
      .replace(/\[思考开始\]/g, '')
      .replace(/\[思考结束\]/g, '')
      .trim();
  };

  return (
    <div className='space-y-4'>
      {/* Attachments - Separate bubble above user message */}
      {attachments && attachments.length > 0 && (
        <div className='flex justify-end' data-testid='turn-attachments'>
          <div className='max-w-[85%] bg-gray-50 rounded-lg p-3 shadow-sm'>
            <div className='flex flex-wrap gap-2'>
              {attachments.map(file => (
                <div
                  key={file.file_id}
                  className='flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors'
                >
                  {/* File Icon */}
                  <div className='flex-shrink-0'>{getFileIcon(file.file_name)}</div>
                  {/* File Info */}
                  <div className='flex-1 min-w-0'>
                    <div
                      className='text-sm font-medium text-gray-900 truncate'
                      title={file.file_name}
                    >
                      {file.file_name}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* User Message - right aligned */}
      <div className='flex justify-end' data-testid='turn-user' data-turn-id={turn.id}>
        <div className='relative group max-w-[85%] rounded-lg p-4 shadow-sm transition-all duration-300 bg-blue-50'>
          <div
            className='text-gray-900 text-sm leading-relaxed whitespace-pre-wrap'
            data-testid='turn-content'
          >
            {turn.content.text}
          </div>

          <div className='flex items-center justify-between gap-2 mt-2'>
            <div className='text-xs text-gray-500'>{formatTime(turn.createdAt)}</div>
          </div>
        </div>
      </div>

      {/* Assistant Answer - left aligned */}
      {hasAnswer || isStreaming || hasError ? (
        <div
          className='flex justify-start -mx-6 sm:mx-0'
          data-testid='turn-assistant'
          data-turn-id={turn.id}
        >
          <div className='relative group max-w-[100%] sm:max-w-full'>
            <div
              className='bg-gray-50 rounded-lg px-6 py-4 sm:p-4 relative'
              data-streaming={isStreaming ? 'true' : 'false'}
            >
              {/* Header: Name, Timestamp, and Stop Button */}
              <div className='flex items-center gap-2 mb-3'>
                <span className='text-sm font-semibold text-gray-900'>
                  {t('chat.aiAssistant', '无境AI')}
                </span>
                <span className='text-xs text-gray-500'>{formatTime(turn.createdAt)}</span>
              </div>

              {/* Thinking Process - 默认展开，可折叠 */}
              {turn.thinking && turn.thinking.trim().length > 0 && (
                <details open className='mb-4 group'>
                  <summary className='flex items-center gap-2 cursor-pointer list-none text-sm text-gray-600 hover:text-gray-800 transition-colors select-none'>
                    <span className='font-semibold'>{t('chat.thinkingProcess', '思考过程')}</span>
                    <span className='text-xs text-gray-400 group-open:rotate-180 transition-transform duration-200'>
                      ▼
                    </span>
                  </summary>
                  <div className='mt-2 p-3 bg-blue-50 rounded-lg text-sm text-gray-700 border border-blue-100 relative prose prose-sm max-w-none prose-p:mb-2 prose-p:text-gray-700 prose-headings:text-gray-800 prose-headings:font-semibold prose-a:text-blue-500'>
                    <ReactMarkdown
                      remarkPlugins={[remarkMath, remarkGfm]}
                      rehypePlugins={[rehypeKatex]}
                      components={{
                        table: ({ children, ...props }) => (
                          <div
                            className='overflow-x-auto my-2'
                            style={{
                              maxWidth: '100%',
                              display: 'block',
                              overflowX: 'auto',
                              WebkitOverflowScrolling: 'touch',
                            }}
                          >
                            <table
                              className='divide-y divide-gray-300'
                              style={{
                                width: 'max-content',
                                display: 'table',
                              }}
                              {...props}
                            >
                              {children}
                            </table>
                          </div>
                        ),
                        thead: ({ children, ...props }) => (
                          <thead className='bg-gray-50' {...props}>
                            {children}
                          </thead>
                        ),
                        th: ({ children, ...props }) => (
                          <th
                            className='px-3 py-1.5 text-left text-xs font-medium text-gray-900 uppercase tracking-wider whitespace-nowrap border border-gray-300'
                            {...props}
                          >
                            {children}
                          </th>
                        ),
                        td: ({ children, ...props }) => (
                          <td
                            className='px-3 py-1.5 whitespace-nowrap text-xs text-gray-700 border border-gray-300'
                            {...props}
                          >
                            {children}
                          </td>
                        ),
                        hr: ({ ...props }) => (
                          <hr className='my-4 border-t border-gray-300' {...props} />
                        ),
                      }}
                    >
                      {cleanThinkingContent(turn.thinking)}
                    </ReactMarkdown>
                  </div>
                </details>
              )}

              {/* Message Content */}
              <div className='mb-4' data-testid='turn-answer'>
                {hasAnswer && (
                  <div className='prose prose-sm max-w-none prose-headings:font-semibold prose-a:text-blue-500 prose-a:no-underline hover:prose-a:underline prose-table:text-gray-900 prose-th:border-gray-300 prose-th:bg-gray-50 prose-td:border-gray-300 prose-p:text-gray-900 prose-p:leading-relaxed prose-p:mb-3'>
                    <ReactMarkdown
                      remarkPlugins={[remarkMath, remarkGfm]}
                      rehypePlugins={[rehypeKatex]}
                      components={{
                        // Custom code component
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        code: ({ className, children, ...props }: any) => {
                          const match = /language-(\w+)/.exec(className || '');
                          const isInline = !className || !match;
                          const language = match ? match[1] : 'text';

                          return !isInline ? (
                            <div className='relative my-2 rounded-lg overflow-hidden border border-gray-300 bg-white'>
                              <div className='flex items-center justify-between px-4 py-2 bg-gray-100 border-b border-gray-300'>
                                <span className='text-sm text-gray-700 font-medium capitalize'>
                                  {language}
                                </span>
                                <button
                                  onClick={() =>
                                    navigator.clipboard.writeText(
                                      String(children).replace(/\n$/, '')
                                    )
                                  }
                                  className='text-gray-500 hover:text-gray-700 transition-colors'
                                  title='复制代码'
                                >
                                  <Copy size={14} />
                                </button>
                              </div>
                              <SyntaxHighlighter
                                language={language}
                                style={tomorrow}
                                PreTag={({ children }) => (
                                  <pre className='p-4 overflow-x-auto m-0'>{children}</pre>
                                )}
                                customStyle={{
                                  margin: 0,
                                  borderRadius: 0,
                                  background: '#ffffff',
                                  fontSize: '14px',
                                  lineHeight: '1.5',
                                }}
                                showLineNumbers={true}
                                wrapLines={true}
                              >
                                {String(children).replace(/\n$/, '')}
                              </SyntaxHighlighter>
                            </div>
                          ) : (
                            <code
                              className='bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono text-gray-800'
                              {...props}
                            >
                              {children}
                            </code>
                          );
                        },
                        // Custom table wrapper for horizontal scroll
                        table: ({ children, ...props }) => (
                          <div
                            className='overflow-x-auto my-3'
                            style={{
                              maxWidth: '100%',
                              display: 'block',
                              overflowX: 'auto',
                              WebkitOverflowScrolling: 'touch',
                            }}
                          >
                            <table
                              className='divide-y divide-gray-300'
                              style={{
                                width: 'max-content',
                                display: 'table',
                              }}
                              {...props}
                            >
                              {children}
                            </table>
                          </div>
                        ),
                        // Custom table header
                        thead: ({ children, ...props }) => (
                          <thead className='bg-gray-50' {...props}>
                            {children}
                          </thead>
                        ),
                        // Custom table header cell
                        th: ({ children, ...props }) => (
                          <th
                            className='px-4 py-2 text-left text-xs font-medium text-gray-900 uppercase tracking-wider whitespace-nowrap border border-gray-300'
                            {...props}
                          >
                            {children}
                          </th>
                        ),
                        // Custom table cell
                        td: ({ children, ...props }) => (
                          <td
                            className='px-4 py-2 whitespace-nowrap text-sm text-gray-900 border border-gray-300'
                            {...props}
                          >
                            {children}
                          </td>
                        ),
                        // Custom horizontal rule with proper spacing
                        hr: ({ ...props }) => (
                          <hr className='my-6 border-t border-gray-300' {...props} />
                        ),
                        // Custom link component
                        a: ({ href, children, ...props }) => (
                          <a
                            href={href}
                            target='_blank'
                            rel='noopener noreferrer'
                            className='text-blue-500 hover:text-blue-600 underline'
                            {...props}
                          >
                            {children}
                          </a>
                        ),
                        // Custom paragraph styling
                        p: ({ children, ...props }) => (
                          <p className='mb-3 text-gray-900 leading-relaxed' {...props}>
                            {children}
                          </p>
                        ),
                      }}
                    >
                      {turn.answerText}
                    </ReactMarkdown>
                  </div>
                )}

                {/* Streaming indicator */}
                {showStreamingAnimation && (
                  <div className='flex items-center gap-1 mt-2'>
                    <div
                      className='w-2 h-2 bg-gray-400 rounded-full animate-bounce'
                      style={{ animationDelay: '0ms' }}
                    ></div>
                    <div
                      className='w-2 h-2 bg-gray-400 rounded-full animate-bounce'
                      style={{ animationDelay: '150ms' }}
                    ></div>
                    <div
                      className='w-2 h-2 bg-gray-400 rounded-full animate-bounce'
                      style={{ animationDelay: '300ms' }}
                    ></div>
                  </div>
                )}
              </div>

              {/* Error display - removed since errors are now shown as toast notifications */}
              {/* Interaction Buttons: Copy, Like, Dislike */}
              <div className='flex items-center gap-2 mb-3'>
                {_onCopy && (
                  <button
                    onClick={() => _onCopy(turn.answerText)}
                    className='p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors'
                    title={t('common.copy', '复制')}
                  >
                    <Copy size={16} />
                  </button>
                )}
                {onLike && (
                  <>
                    <button
                      onClick={() => onLike('like')}
                      className={`p-1.5 rounded-lg transition-colors ${
                        likeStatus === 'like'
                          ? 'bg-blue-100 text-blue-600'
                          : 'text-gray-400 hover:bg-gray-100 hover:text-gray-600'
                      }`}
                      title={t('common.like', '点赞')}
                    >
                      <ThumbsUp size={16} />
                    </button>
                    <button
                      onClick={() => onLike('dislike')}
                      className={`p-1.5 rounded-lg transition-colors ${
                        likeStatus === 'dislike'
                          ? 'bg-red-100 text-red-600'
                          : 'text-gray-400 hover:bg-gray-100 hover:text-gray-600'
                      }`}
                      title={t('common.dislike', '点踩')}
                    >
                      <ThumbsDown size={16} />
                    </button>
                  </>
                )}
              </div>

              {/* Suggested Actions */}
              {showSuggestions && (
                <>
                  {suggestedActionsLoading ? (
                    <SuggestionSkeleton count={3} />
                  ) : suggestedActions.length > 0 ? (
                    <div className='space-y-2 pt-3'>
                      {suggestedActions.map((action, idx) => (
                        <button
                          key={idx}
                          onClick={() => onSuggestedAction && onSuggestedAction(action)}
                          className='w-full text-left p-3 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors flex items-start gap-2'
                        >
                          <Sparkle size={16} className='text-blue-500 mt-0.5 flex-shrink-0' />
                          <span className='text-sm text-blue-500 flex-1'>{action}</span>
                        </button>
                      ))}
                    </div>
                  ) : null}
                </>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default TurnView;
